import React, { useCallback, useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, GitBranch, PlusIcon, Banknote, Edit } from 'lucide-react';
// import services
import { getUserList } from '../services/admin.service';
// import custom components
import DataTable from '../custom/DataTable';
// import components
import AddManualUser from '../components/AddManualUser';
import EditUser from '../components/EditUser';
import BankAccountModal from '../components/BankAccountModal';
// import lib
import { dateTimeFormat } from '../lib/dateHelper';

const UserList = () => {

    // hooks
    const navigate = useNavigate();
    const columns = [
        {
            key: "userName",
            text: "Name",
            className: "text-left",
            align: "left",
            sortable: false
        },
        {
            key: "email",
            text: "Email",
            className: "text-left",
            align: "left",
            sortable: false
        },
        {
            key: "phone",
            text: "Phone",
            className: "text-left",
            align: "left",
            sortable: false
        },
        {
            key: "originalPassword",
            text: "Password",
            className: "text-left",
            align: "left",
            sortable: false
        },
        {
            key: "userCode",
            text: "User Id",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ userCode }) => {
                return (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {userCode}
                    </span>
                )
            }
        },
        {
            key: "pinNumber",
            text: "PIN Number",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ pinNumber }) => {
                return (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {pinNumber || 'NULL'}
                    </span>
                )
            }
        },
        {
            key: "createdAt",
            text: "Created At",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ createdAt }) => {
                return createdAt ? dateTimeFormat(createdAt) : 'NULL';
            }
        },
        {
            key: "action",
            text: "Action",
            className: "text-center",
            align: "left",
            sortable: false,
            cell: ({ userId, _id, userName, originalPassword, phone, email }) => {
                return (
                    <Fragment>
                        <button
                            onClick={() => navigate(`/binary-tree/${userId}`)}
                            className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                            <GitBranch className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => {
                                setIsBankModalOpen(true)
                                setUpdateUsrId(_id)
                            }}
                            className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                            <Banknote className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => {
                                setShowEditModal(true);
                                setUpdateUsrId(_id)
                                setEditUserValue({
                                    userName, phone, email, password: originalPassword
                                });
                            }}
                            className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                    </Fragment>
                )
            }
        }
    ];
    // state
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [loader, setLoader] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, search: "" });
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [updateUsrId, setUpdateUsrId] = useState('');
    const [editUserValue, setEditUserValue] = useState({});

    const fetchData = useCallback(async (pagination) => {
        try {
            setLoader(true);
            const { status, result } = await getUserList(pagination);
            setLoader(false);
            if (status === true) {
                setData(result.data)
                setCount(result.count)
            }
        } catch (err) { }
    })

    useEffect(() => {
        fetchData(pagination)
    }, [pagination])

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">User List</h1>
                                <p className="text-gray-600 text-sm">Manage and view all registered users</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold transition-all hover:from-cyan-600 hover:to-blue-700 shadow-md hover:shadow-lg"
                            >
                                <PlusIcon className="w-4 h-4" />
                                Manual User
                            </button>
                        </div>
                    </div>
                </div>
                <DataTable
                    columns={columns}
                    data={data}
                    count={count}
                    loader={loader}
                    setPagination={setPagination}
                />
                <AddManualUser
                    showModal={showModal}
                    setShowModal={setShowModal}
                    fetchData={fetchData}
                />
                <EditUser
                    showModal={showEditModal}
                    setShowModal={setShowEditModal}
                    userId={updateUsrId}
                    value={editUserValue}
                    fetchData={fetchData}
                />
                <BankAccountModal
                    isOpen={isBankModalOpen}
                    onClose={() => setIsBankModalOpen(false)}
                    userId={updateUsrId}
                    fetchData={fetchData}
                />
            </div>
        </div>
    );
};

export default UserList;